// frontend/src/hooks/useHandbookData.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { configAPI } from '../services/api';

/**
 * Fetches the current published employee handbook edition.
 *
 * Returns the same shape as the static config module
 * (frontend/src/config/handbookContent.js): { meta, parts, sections,
 * documentControl }. The reader falls back to that static module if this
 * request fails, so the handbook is always readable.
 */
export const useHandbook = () => {
  return useQuery({
    queryKey: ['handbook', 'current'],
    queryFn: async () => {
      const response = await configAPI.getHandbook();
      return response.data;
    },
    // The handbook changes rarely (published editions), so keep it fresh
    // for a long time and serve from cache aggressively.
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 1,
  });
};

// ---------------------------------------------------------------------------
// Admin CMS hooks
// ---------------------------------------------------------------------------

/** List all handbook sections (with current revision + latest draft summaries). */
export const useHandbookSections = (params = {}) => {
  return useQuery({
    queryKey: ['handbook', 'admin', 'sections', params],
    queryFn: async () => {
      const response = await configAPI.getHandbookSections(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

/** Full detail (including blocks) for a single revision. */
export const useHandbookRevision = (revisionId) => {
  return useQuery({
    queryKey: ['handbook', 'admin', 'revision', revisionId],
    queryFn: async () => {
      const response = await configAPI.getHandbookRevision(revisionId);
      return response.data;
    },
    enabled: !!revisionId,
    staleTime: 30 * 1000,
  });
};

/** Full revision history (audit timeline) for one section. */
export const useHandbookSectionRevisions = (sectionId) => {
  return useQuery({
    queryKey: ['handbook', 'admin', 'revisions', sectionId],
    queryFn: async () => {
      const response = await configAPI.getHandbookSectionRevisions(sectionId);
      return response.data || [];
    },
    enabled: !!sectionId,
    staleTime: 60 * 1000,
  });
};

const invalidateHandbook = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['handbook'] });
};

/** Start (or resume) a draft revision for a section. */
export const useCreateHandbookDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId) => configAPI.createHandbookDraft(sectionId),
    onSuccess: () => {
      invalidateHandbook(queryClient);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to start draft');
    },
  });
};

/** Save edits to a draft revision. */
export const useUpdateHandbookRevision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ revisionId, data }) => configAPI.updateHandbookRevision(revisionId, data),
    onSuccess: () => {
      invalidateHandbook(queryClient);
      toast.success('Draft saved');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to save draft');
    },
  });
};

/** Workflow transitions: submit / approve / publish. */
export const useHandbookRevisionTransition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ revisionId, action }) => {
      if (action === 'submit') return configAPI.submitHandbookRevision(revisionId);
      if (action === 'approve') return configAPI.approveHandbookRevision(revisionId);
      if (action === 'publish') return configAPI.publishHandbookRevision(revisionId);
      throw new Error(`Unknown handbook transition: ${action}`);
    },
    onSuccess: (_data, variables) => {
      invalidateHandbook(queryClient);
      const labels = { submit: 'Submitted for review', approve: 'Approved', publish: 'Published' };
      toast.success(labels[variables.action] || 'Updated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Action failed');
    },
  });
};

// ---------------------------------------------------------------------------
// Edition hooks (formal releases / Document Control)
// ---------------------------------------------------------------------------

/** List all handbook editions (newest first). */
export const useHandbookEditions = () => {
  return useQuery({
    queryKey: ['handbook', 'admin', 'editions'],
    queryFn: async () => {
      const response = await configAPI.getHandbookEditions();
      return response.data?.results || response.data || [];
    },
    staleTime: 2 * 60 * 1000,
  });
};

/** Create a new draft edition. */
export const useCreateHandbookEdition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => configAPI.createHandbookEdition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handbook'] });
      toast.success('Edition created');
    },
    onError: (error) => {
      const data = error?.response?.data;
      const msg = data?.code?.[0] || data?.detail || 'Failed to create edition';
      toast.error(msg);
    },
  });
};

/** Update a draft edition's metadata (code, effective date, summary, etc.). */
export const useUpdateHandbookEdition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ editionId, data }) => configAPI.updateHandbookEdition(editionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handbook'] });
      toast.success('Edition saved');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to save edition');
    },
  });
};

/**
 * Publish an edition: snapshots the live revisions, captures the
 * "Changes in this edition" log, and makes it the current edition employees
 * acknowledge.
 */
export const usePublishHandbookEdition = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (editionId) => configAPI.publishHandbookEdition(editionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handbook'] });
      toast.success('Edition published');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Failed to publish edition');
    },
  });
};

// ---------------------------------------------------------------------------
// Acknowledgement hooks
// ---------------------------------------------------------------------------

/** Current employee's acknowledgement status + "what's new" for the live edition. */
export const useHandbookAcknowledgement = () => {
  return useQuery({
    queryKey: ['handbook', 'acknowledgement', 'me'],
    queryFn: async () => {
      const response = await configAPI.getHandbookAcknowledgement();
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

/** Sign off the current handbook edition. */
export const useAcknowledgeHandbook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => configAPI.acknowledgeHandbook(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handbook', 'acknowledgement'] });
      toast.success('Handbook acknowledged. Thank you!');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.detail || 'Could not record acknowledgement');
    },
  });
};

/** HR compliance dashboard: who has / hasn't acknowledged the live edition. */
export const useHandbookAckDashboard = () => {
  return useQuery({
    queryKey: ['handbook', 'acknowledgement', 'dashboard'],
    queryFn: async () => {
      const response = await configAPI.getHandbookAckDashboard();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
  });
};

export default useHandbook;
